// @ts-nocheck
/**
 * Industry Configuration Registry
 *
 * Central registry for all supported industries.
 * Add new industries by importing their config and adding to the registry.
 */

import { IndustryConfig, IndustryId } from '../industryTypes'
import { manufacturingConfig } from './manufacturing'
import { oilGasConfig } from './oilgas'
import { healthcareConfig } from './healthcare'
import { financialConfig } from './financial'
import { energyConfig } from './energy'
import { transportationConfig } from './transportation'
import { miningConfig } from './mining'
import { chemicalsConfig } from './chemicals'
import { foodBeverageConfig } from './food_beverage'
import { pharmaceuticalsConfig } from './pharmaceuticals'
import { aerospaceConfig } from './aerospace'
import { metalsConfig } from './metals'
import { iotTelecomConfig } from './iot_telecom'
import { utilitiesConfig } from './utilities'
import { pulpPaperConfig } from './pulp_paper'
import { automotiveConfig } from './automotive'
import { datacenterConfig } from './datacenter'
import { facilityManagementConfig } from './facility_management'
// 2026-06-27 — non-industrial + medical-grade verticals
import { aquacultureLobsterConfig } from './aquaculture_lobster'
// 2026-07-02 — deprecated: replaced by wearable_etd (was mis-scoped as medical;
// actual requirement is explosive trace detection). Import kept out of the
// static registry; backend rows stay harmless.
// import { biosensorIdeWearableConfig } from './biosensor_ide_wearable'
// 2026-07-02 — Wearable ETD (Explosive Trace Detection) security/defense vertical
import { wearableEtdConfig } from './wearable_etd'

// =============================================================================
// INDUSTRY REGISTRY
// =============================================================================

/**
 * Registry of all supported industries
 */
export const industryRegistry: Partial<Record<IndustryId, IndustryConfig>> = {
  manufacturing: manufacturingConfig,
  oil_gas: oilGasConfig,
  healthcare: healthcareConfig,
  financial: financialConfig,
  energy: energyConfig,
  transportation: transportationConfig,
  mining: miningConfig,
  chemicals: chemicalsConfig,
  food_beverage: foodBeverageConfig,
  pharmaceuticals: pharmaceuticalsConfig,
  aerospace: aerospaceConfig,
  metals: metalsConfig,
  iot_telecom: iotTelecomConfig,
  utilities: utilitiesConfig,
  pulp_paper: pulpPaperConfig,
  automotive: automotiveConfig,
  datacenter: datacenterConfig,
  facility_management: facilityManagementConfig,
  // 2026-06-27 — non-industrial + medical-grade verticals
  aquaculture_lobster: aquacultureLobsterConfig,
  // 2026-07-02 — deprecated: replaced by wearable_etd (was mis-scoped as medical;
  // actual requirement is explosive trace detection). Removed from the static
  // picker; backend rows stay harmless.
  // biosensor_ide_wearable: biosensorIdeWearableConfig,
  // 2026-07-02 — Wearable ETD (Explosive Trace Detection) security/defense vertical
  wearable_etd: wearableEtdConfig,
}

/**
 * Dynamic industry configs loaded from API at runtime
 */
const dynamicIndustryRegistry: Record<string, IndustryConfig> = {}

/**
 * List of all available industry IDs (static + dynamic)
 */
export function getAvailableIndustries(): IndustryId[] {
  const staticIds = Object.keys(industryRegistry) as IndustryId[]
  const dynamicIds = Object.keys(dynamicIndustryRegistry).filter(
    id => !staticIds.includes(id as IndustryId)
  ) as IndustryId[]
  return [...staticIds, ...dynamicIds]
}

/**
 * @deprecated Use getAvailableIndustries() for dynamic support
 */
export const availableIndustries: IndustryId[] = Object.keys(industryRegistry) as IndustryId[]

/**
 * Default industry (used when none is specified)
 * Oil & Gas is the baseline/reference industry
 * Users can change industry via Settings UI (stored in localStorage)
 */
export const defaultIndustryId: IndustryId = 'oil_gas'

/**
 * 2026-07-02 — industries deprecated from the picker. Backend DB rows may
 * still exist (harmless), but the dynamic loader must NOT surface them.
 * `biosensor_ide_wearable` was mis-scoped as a medical vertical; it is
 * replaced by `wearable_etd` (Explosive Trace Detection). Because it was
 * removed from the static `industryRegistry`, the dynamic loader would
 * otherwise re-add it — this deny-list prevents that.
 */
const DEPRECATED_INDUSTRY_CODES = new Set<string>([
  'biosensor_ide_wearable',
  // 2026-07-13 — `pharma` is a near-empty duplicate of the real
  // `pharmaceuticals` industry. Deny it so only `pharmaceuticals` surfaces.
  'pharma',
])

/**
 * Load dynamic industries from the backend API.
 * Called on app startup to hydrate dynamically registered industries.
 */
export async function loadDynamicIndustries(): Promise<void> {
  try {
    const response = await fetch('/api/v1/industries/config/frontend')
    if (!response.ok) return
    const data = await response.json()
    if (!data?.industries) return
    for (const ind of data.industries) {
      if (!ind.code) continue
      // Skip deprecated industries (see DEPRECATED_INDUSTRY_CODES)
      if (DEPRECATED_INDUSTRY_CODES.has(ind.code)) continue
      // Skip industries already statically baked into the build
      if (industryRegistry[ind.code as IndustryId]) continue
      // Skip industries already hydrated this session — `hydrateIndustryFromBackend`
      // produces the richer config; don't clobber it with the bulk-list summary.
      if (dynamicIndustryRegistry[ind.code]) continue

      const assetTypes = (ind.asset_types || []).map((at: any) => ({
        id: at.code || at.id,
        name: at.name,
        category: at.category || 'dynamic',
        sensors: at.sensors || [],
        predictions: at.predictions || [],
      }))
      const kpiDomains = (ind.kpis || []).map((k: any) => ({
        id: k.id || k.code,
        name: k.name,
        category: k.category || 'industry_specific',
        kpis: k.kpis || [],
      }))
      const complianceStandards = ((ind.compliance && ind.compliance.standards) || []).map((s: any) =>
        typeof s === 'string' ? { id: s.toLowerCase().replace(/\s+/g, '_'), name: s } : s
      )

      dynamicIndustryRegistry[ind.code] = {
        id: ind.code,
        name: ind.name || ind.code,
        description: ind.description || `${ind.name || ind.code} industry`,
        icon: ind.icon || 'Building2',
        color: ind.color || '#6b7280',
        terminology: ind.terminology || {},
        assetTypes,
        kpiDomains,
        maintenanceTypes: [],
        complianceStandards,
        roles: [],
        explanationStyles: [],
        partsCatalog: { categories: [] },
        rootCauseLibrary: { categories: [] },
        laborRates: { categories: [] },
      } as unknown as IndustryConfig
    }
  } catch {
    // Non-fatal — static industries still work
  }
}

/**
 * Hydrate ONE industry from the new backend frontend-config endpoint
 * (`GET /api/v2/data/industries/{id}/frontend-config`).
 *
 * Unlike `loadDynamicIndustries()` (which only fills empty arrays), this
 * pulls the full IndustryAdapterService output: terminology, kpi_domains,
 * professional_roles, compliance_standards, maintenance_types, and
 * narrative_templates — so the IndustryContext shows real labels even for
 * a brand-new industry like share_market.
 *
 * Returns true if hydration succeeded.
 */
export async function hydrateIndustryFromBackend(id: string): Promise<boolean> {
  if (!id) return false
  try {
    const r = await fetch(`/api/v2/data/industries/${encodeURIComponent(id)}/frontend-config`)
    if (!r.ok) return false
    const c = await r.json()
    if (!c?.code) return false
    dynamicIndustryRegistry[c.code] = {
      id: c.code,
      name: c.name || c.code,
      description: c.description || '',
      icon: 'Building2',
      color: '#6b7280',
      terminology: c.terminology || {},
      assetTypes: c.asset_types || [],
      kpiDomains: c.kpi_domains || [],
      maintenanceTypes: c.maintenance_types || [],
      complianceStandards: c.compliance_standards || [],
      roles: c.professional_roles || [],
      professionalRoles: c.professional_roles || [],
      explanationStyles: [],
      partsCatalog: { categories: [] },
      rootCauseLibrary: { categories: [] },
      laborRates: { categories: [] },
      narrativeTemplates: c.narrative_templates || {},
      riskFactors: c.risk_factors || [],
    } as unknown as IndustryConfig
    return true
  } catch {
    return false
  }
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get industry configuration by ID
 * Checks dynamic registry first, then static, falls back to default
 */
export function getIndustryConfig(id: IndustryId | string): IndustryConfig {
  return dynamicIndustryRegistry[id] || industryRegistry[id as IndustryId] || industryRegistry[defaultIndustryId]
}

/**
 * Get industry name by ID
 */
export function getIndustryName(id: IndustryId): string {
  return industryRegistry[id]?.name || 'Unknown'
}

/**
 * Get industry icon by ID
 */
export function getIndustryIcon(id: IndustryId): string {
  return industryRegistry[id]?.icon || 'HelpCircle'
}

/**
 * Check if an industry ID is valid
 */
export function isValidIndustry(id: string): id is IndustryId {
  return id in industryRegistry || id in dynamicIndustryRegistry
}

/**
 * 2026-07-13 — wind / defence / nuclear are served by the backend (NAB
 * datasets) but ship with no static frontend config file, so they never
 * appeared in the industry picker (which iterates the static registry).
 * Surface them explicitly here so all 24 industries are selectable; picking
 * one hydrates its full config from the backend (see IndustryContext).
 */
const EXTRA_PICKER_INDUSTRIES: Array<{
  id: string
  name: string
  description: string
  icon: string
}> = [
  { id: 'wind', name: 'Wind', description: 'Wind farm turbine monitoring and predictive maintenance', icon: 'Zap' },
  { id: 'defence', name: 'Defence', description: 'Defence program oversight and CMMC asset assurance', icon: 'Building2' },
  { id: 'nuclear', name: 'Nuclear', description: 'Nuclear reactor oversight and NRC ROP cornerstones', icon: 'Building2' },
]

/**
 * Get all industry summaries for selection UI
 */
export function getIndustrySummaries(): Array<{
  id: IndustryId
  name: string
  description: string
  icon: string
  assetTypeCount: number
  kpiDomainCount: number
}> {
  // Exclude `pharma` (near-empty duplicate of `pharmaceuticals`).
  const summaries = availableIndustries
    .filter(id => id !== 'pharma')
    .map(id => {
      const config = industryRegistry[id]
      // Defensive: dynamically-registered industries (aqua, biosensor) ship as
      // minimal stubs without assetTypes/kpiDomains. Don't crash IndustrySelector.
      return {
        id,
        name: config?.name ?? id,
        description: config?.description ?? '',
        icon: config?.icon ?? 'HelpCircle',
        assetTypeCount: Array.isArray(config?.assetTypes) ? config.assetTypes.length : 0,
        kpiDomainCount: Array.isArray((config as any)?.kpiDomains) ? (config as any).kpiDomains.length : 0,
      }
    })

  // Append backend-served industries that lack a static config file.
  for (const extra of EXTRA_PICKER_INDUSTRIES) {
    if (!summaries.some(s => s.id === extra.id)) {
      summaries.push({
        id: extra.id as IndustryId,
        name: extra.name,
        description: extra.description,
        icon: extra.icon,
        assetTypeCount: 0,
        kpiDomainCount: 0,
      })
    }
  }

  return summaries
}

/**
 * Get merged terminology from multiple industries (for migration/comparison)
 */
export function getMergedTerminology(): Record<string, Set<string>> {
  const merged: Record<string, Set<string>> = {}

  for (const industry of Object.values(industryRegistry)) {
    for (const [key, value] of Object.entries(industry.terminology)) {
      if (!merged[key]) {
        merged[key] = new Set()
      }
      merged[key].add(value)
    }
  }

  return merged
}

/**
 * Get all unique asset types across industries
 */
export function getAllAssetTypes(): Array<{
  id: string
  name: string
  industryId: IndustryId
  category: string
}> {
  const assetTypes: Array<{
    id: string
    name: string
    industryId: IndustryId
    category: string
  }> = []

  for (const [industryId, config] of Object.entries(industryRegistry)) {
    for (const assetType of config.assetTypes) {
      assetTypes.push({
        id: assetType.id,
        name: assetType.name,
        industryId: industryId as IndustryId,
        category: assetType.category,
      })
    }
  }

  return assetTypes
}

/**
 * Get all unique KPI domains across industries
 */
export function getAllKPIDomains(): Array<{
  id: string
  name: string
  industryId: IndustryId
  category: 'universal' | 'industry_specific'
}> {
  const domains: Array<{
    id: string
    name: string
    industryId: IndustryId
    category: 'universal' | 'industry_specific'
  }> = []

  const seen = new Set<string>()

  for (const [industryId, config] of Object.entries(industryRegistry)) {
    for (const domain of config.kpiDomains) {
      if (!seen.has(domain.id)) {
        seen.add(domain.id)
        domains.push({
          id: domain.id,
          name: domain.name,
          industryId: industryId as IndustryId,
          category: domain.category,
        })
      }
    }
  }

  return domains
}

// =============================================================================
// EXPORTS
// =============================================================================

export {
  manufacturingConfig,
  oilGasConfig,
  healthcareConfig,
  financialConfig,
  energyConfig,
  transportationConfig,
  miningConfig,
  chemicalsConfig,
  foodBeverageConfig,
  pharmaceuticalsConfig,
  aerospaceConfig,
  metalsConfig,
  iotTelecomConfig,
  utilitiesConfig,
  pulpPaperConfig,
  automotiveConfig,
  datacenterConfig,
}
